import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { getPartnerCarrierWebhookConfig } from 'store/thunks/partnerThunks';

import SwitchField from 'common/forms/SwitchField';
import { isEqual } from 'lodash';

const CARRIER_WEBHOOK_TOGGLES = [
  { key: 'pickedUp', label: 'Send Webhook on Picked Up' },
  { key: 'outForDelivery', label: 'Send Webhook on Out for Delivery' },
  { key: 'delivered', label: 'Send Webhook on Delivered' },
];

const DEFAULT_CARRIER_WEBHOOK_CONFIG = {
  pickedUp: false,
  outForDelivery: false,
  delivered: false,
};

const PartnerWebhookConfigModal = ({
  showModal,
  loading,
  handleClose,
  partner,
  handleCarrierWebhookConfig,
}) => {
  const dispatch = useDispatch();
  const [configLoading, setConfigLoading] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState(DEFAULT_CARRIER_WEBHOOK_CONFIG);
  const initialWebhookConfigRef = useRef(DEFAULT_CARRIER_WEBHOOK_CONFIG);

  const fetchPartnerCarrierWebhookConfig = useCallback(
    (partnerUuid) => {
      setConfigLoading(true);
      dispatch(getPartnerCarrierWebhookConfig(partnerUuid))
        .then((carrierWebhookConfig) => {
          if (carrierWebhookConfig) {
            const nextConfig = {
              pickedUp: !!carrierWebhookConfig.pickedUp,
              outForDelivery: !!carrierWebhookConfig.outForDelivery,
              delivered: !!carrierWebhookConfig.delivered,
            };
            initialWebhookConfigRef.current = nextConfig;
            setWebhookConfig(nextConfig);
          }
        })
        .finally(() => setConfigLoading(false));
    },
    [dispatch]
  );

  const handleWebhookToggle = (key) => setWebhookConfig((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    fetchPartnerCarrierWebhookConfig(partner.partnerUuid);
  }, [partner, fetchPartnerCarrierWebhookConfig]);

  const webhookConfigChanged = !isEqual(webhookConfig, initialWebhookConfigRef.current);

  const handleConfirm = () => {
    handleCarrierWebhookConfig({ partnerUuid: partner.partnerUuid, ...webhookConfig });
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      aria-describedby='Partner-webhook-config'
      fullWidth
    >
      <DialogTitle sx={{ p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
        {`Edit Webhook Config - ${partner.partnerName}`}
      </DialogTitle>

      <DialogContent sx={{ minWidth: 500, p: 3, mt: 3 }}>
        {configLoading ? (
          <Grid container sx={{ minHeight: 200 }} justifyContent='center' alignItems='center'>
            <CircularProgress color='primary' size={50} />
          </Grid>
        ) : (
          <Grid container spacing={2} sx={{ px: 4, py: 2 }}>
            {CARRIER_WEBHOOK_TOGGLES.map(({ key, label }) => (
              <Grid item xs={12} key={key} container alignItems='center' sx={{ mt: 1 }}>
                <Grid item xs>
                  <Typography variant='subtitle1'>{label}</Typography>
                </Grid>
                <Grid item>
                  <SwitchField
                    label={label}
                    checked={!!webhookConfig?.[key]}
                    onClick={() => handleWebhookToggle(key)}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          loading={loading}
          disabled={configLoading || loading || !webhookConfigChanged}
          onClick={handleConfirm}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerWebhookConfigModal;

PartnerWebhookConfigModal.propTypes = {
  partner: PropTypes.shape({
    partnerUuid: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
  }).isRequired,
  showModal: PropTypes.bool,
  loading: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleCarrierWebhookConfig: PropTypes.func.isRequired,
};

PartnerWebhookConfigModal.defaultProps = {
  showModal: false,
  loading: false,
};
